import useManagePlan from '@hooks/useManagePlan';
import {
    Alert,
    AlertTitle,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    LinearProgress,
    Stack,
    Typography,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { EventRepeatRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@api/apiServices';

const CurrentPlan = ({ emailPlan }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const openManagePlan = useManagePlan();

    const scheduledToCancel =
        emailPlan.subscription_data?.scheduled_change?.action?.value ===
        'cancel';
    const scheduledToDowngrade =
        Boolean(emailPlan.subscription_data?.custom_data?.data?.next_plan) &&
        emailPlan.subscription_data?.scheduled_change?.action?.value ===
            'pause';

    const { mutate: cancelChanges, isPending: isCancelling } = useMutation({
        mutationKey: ['cancel-subscription-scheduled-changes'],
        mutationFn: () =>
            api.settings.billing['cancel-scheduled-change'].post(),
        onSuccess: () => {
            queryClient.setQueryData(['settings'], old => {
                const optomisticData = old;
                optomisticData.email_plan.subscription_data.scheduled_change =
                    null;
                return optomisticData;
            });
        },
    });

    return (
        <>
            <Card sx={{ flex: 1, p: 2 }}>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                        <Stack spacing={1}>
                            <Typography
                                variant="labelLargeProminent"
                                textTransform="uppercase"
                                color="GrayText"
                            >
                                {t('Current Plan')}
                            </Typography>
                            <Typography
                                variant="headlineLarge"
                                fontWeight={700}
                                color="primary.main"
                            >
                                {/* {settings.usage_credit.toLocaleString()} */}
                                {emailPlan.monthly_emails.toShorthand()}{' '}
                                {t('emails-per-month')}
                            </Typography>
                        </Stack>
                        <Button onClick={openManagePlan}>
                            {t('Manage Plan')}
                        </Button>
                    </Box>
                    {scheduledToCancel || scheduledToDowngrade ? (
                        <Alert
                            variant="standard"
                            severity="info"
                            sx={{
                                border: '1px solid rgb(51, 130, 171)',
                                my: 3,
                            }}
                            icon={
                                <Avatar
                                    variant="roundedSmall"
                                    sx={{ bgcolor: 'primary.main' }}
                                >
                                    <EventRepeatRounded />
                                </Avatar>
                            }
                            action={
                                <LoadingButton
                                    color="surface"
                                    variant="tonal"
                                    sx={{
                                        color: 'primary.main',
                                        whiteSpace: 'nowrap',
                                    }}
                                    onClick={cancelChanges}
                                    loading={isCancelling}
                                >
                                    {t('Cancel Change')}
                                </LoadingButton>
                            }
                        >
                            <AlertTitle>
                                {t('Subscription change scheduled')}
                            </AlertTitle>
                            <Trans
                                values={{
                                    currentMonthlyEmails:
                                        emailPlan.monthly_emails.toLocaleString(),
                                    upcomingMonthlyEmails: scheduledToCancel
                                        ? 50
                                        : parseInt(
                                              emailPlan.subscription_data
                                                  .custom_data.data.next_plan
                                                  .custom_data.data
                                                  .monthly_emails,
                                          ).toLocaleString(),
                                    upcomingDate: scheduledToCancel
                                        ? emailPlan.subscription_data.scheduled_change.effective_at
                                              .toDate()
                                              .toLocalMediumDateTime()
                                        : emailPlan.subscription_data.scheduled_change.effective_at
                                              .toDate()
                                              .toLocalMediumDateTime(),
                                }}
                            >
                                subscription-changes-scheduled-message
                            </Trans>
                        </Alert>
                    ) : (
                        <Typography
                            variant="bodyMedium"
                            color="text.secondary"
                            mb={3}
                        >
                            {t('Renewal on')}:{' '}
                            {emailPlan.billing_period_end
                                .toDate()
                                .toLocalMediumDateTime()}
                        </Typography>
                    )}
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant="titleMedium">
                            {t('Usage This Month')}
                        </Typography>
                        <Typography variant="titleMedium">
                            {emailPlan.monthly_usage.toLocaleString()} /{' '}
                            {emailPlan.monthly_emails.toLocaleString()}
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={
                            (emailPlan.monthly_usage /
                                emailPlan.monthly_emails) *
                            100
                        }
                        sx={{
                            height: 12,
                            my: 2,
                            borderRadius: 99,
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 99,
                            },
                        }}
                    />
                    <Typography
                        variant="bodySmall"
                        color="text.secondary"
                        mt={0.5}
                    >
                        {t('email_percentage_used', {
                            percentage: Math.floor(
                                (emailPlan.monthly_usage /
                                    emailPlan.monthly_emails) *
                                    100,
                            ),
                        })}
                    </Typography>
                </CardContent>
            </Card>
        </>
    );
};

export default CurrentPlan;
